import React, { useEffect, useRef } from 'react';
import { init2DMap } from '@/utils/initMap';
import carNormal1 from '@/assets/img/views/map/car_normal_1.svg';
import stationImg from '@/assets/img/views/map/station.svg';
import CustomOverlay from '@/components/MapMode/CustomOverlay';
import './simple.scss';

const win = window as any;

// 业务数据为 GCJ02 坐标，BMapGL 已按 BD09 解析，需先转换（与 MapMode 保持一致）
const toBD09 = (lat: number, lon: number) => {
  if (win.coordtransform) {
    try {
      const bd = win.coordtransform().gcj02tobd09(lon, lat);
      return { lat: bd[1], lon: bd[0] };
    } catch (e) {
      console.warn('[SimpleMap] coordtransform failed, using original coords', e);
    }
  }
  return { lat, lon };
};

const SimpleMap = (props: {
  lon: number;
  lat: number;
  width?: string;
  height?: string;
  name?: string;
  centerPoint?: {
    lon: number;
    lat: number;
    name?: string;
  };
}) => {
  const _simpleMapContainer = useRef<HTMLDivElement>(null);
  const _mapRef = useRef<any>(null);
  // 记录已添加的覆盖物，重绘前清理，避免 effect 重复执行时 marker 叠加
  const _overlaysRef = useRef<any[]>([]);
  const { lon, lat, width, height, name, centerPoint } = props;

  const clearOverlays = () => {
    const map = _mapRef.current;
    if (!map) return;
    _overlaysRef.current.forEach((overlay) => {
      overlay.setMap ? overlay.setMap(null) : map.removeOverlay(overlay);
    });
    _overlaysRef.current = [];
  };

  const drawMarker = (
    lat: number,
    lon: number,
    iconImg: string,
    name?: string,
  ) => {
    const map = _mapRef.current;
    const { BMapGL } = win;
    if (!map || !BMapGL) return;
    const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
    const point = new BMapGL.Point(bdLon, bdLat);
    const size = new BMapGL.Size(40, 42);
    const marker = new BMapGL.Marker(point, {
      icon: new BMapGL.Icon(iconImg, size, {
        anchor: new BMapGL.Size(10, 20),
      }),
    });
    map.addOverlay(marker);
    _overlaysRef.current.push(marker);
    if (name) {
      const overlay: any = new CustomOverlay(
        `<div class="overlay-container"><span class="label-text">${name}</span></div>`,
        point,
        20,
      );
      overlay.setMap(map);
      _overlaysRef.current.push(overlay);
    }
    map.setCenter(point);
  };

  useEffect(() => {
    const { BMapGL } = win;
    if (!BMapGL || !_simpleMapContainer.current) return;
    const { lat: bdLat, lon: bdLon } = toBD09(34.53506, 108.93885);
    _mapRef.current = init2DMap(
      { lat: bdLat, lon: bdLon },
      _simpleMapContainer.current,
    );
    _mapRef.current && _mapRef.current.setZoom(15);
  }, []);

  // 车辆点与站点中心点可能同时存在（如单车页站点弹层），统一在一个 effect 中先清后画；
  // 站点后画，保证两者同时存在时地图中心落在站点上（与原有两个 effect 的执行顺序一致）
  // 注意：依赖取具体值而非 centerPoint 对象，调用方内联传对象会导致引用每次渲染都变
  const centerLat = centerPoint?.lat;
  const centerLon = centerPoint?.lon;
  const centerName = centerPoint?.name;
  useEffect(() => {
    clearOverlays();
    if (lat && lon) {
      drawMarker(lat, lon, carNormal1, name);
    }
    if (centerLat && centerLon) {
      drawMarker(centerLat, centerLon, stationImg, centerName);
    }
  }, [lat, lon, name, centerLat, centerLon, centerName]);

  return (
    <div
      ref={_simpleMapContainer}
      style={{
        width: props.width || '1030px',
        height: props.height || '500px',
        position: 'relative',
      }}
    />
  );
};

export default React.memo(SimpleMap);
